/* eslint-disable @typescript-eslint/no-explicit-any */
import { NgClass } from '@angular/common';
import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator
} from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'input-select',
  standalone: true,
  imports: [NgSelectModule, TranslateModule, FormsModule, ReactiveFormsModule, NgClass],
  templateUrl: './input-select.component.html',
  styleUrl: './input-select.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputSelectComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputSelectComponent),
      multi: true
    }
  ]
})
export class InputSelectComponent implements ControlValueAccessor, Validator, OnInit {
  @Input() items: any[] = [];
  @Input() bindLabel: string = '';
  @Input() bindValue: string = '';
  @Input() placeholder: string = 'Выберите значение';
  @Input() labelForId: string = '';
  @Input() labelName: string = '';
  @Input() searchable: boolean = true;
  @Input() editableSearchTerm: boolean = false;
  @Input() multiple: boolean = false;
  @Input() clearable: boolean = true;
  @Input() readonly: boolean = false;
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() hideSelected: boolean = false;
  @Input() hideRemoveButton: boolean = false;
  @Input() notFoundText: string = 'Ничего не найдено';
  @Input() loadingText: string = 'Загрузка...';
  @Input() id: string = '';
  @Input() name: string = '';
  @Input() errorMessage = 'Поле обязательно для заполнения';
  @Input() preventRemoveLastItem: boolean = false;
  @Input() value: any = null;
  @Input() control = new FormControl();
  @Input() loading = false;
  @Input() scrollable = false;

  @Output() valueChanged = new EventEmitter<any>();
  @Output() searchValueChanged = new EventEmitter<any>();
  @Output() valueCleared = new EventEmitter<any>();
  @Output() valueRemoved = new EventEmitter<any>();
  @Output() valueAdded = new EventEmitter<any>();
  @Output() scrolled = new EventEmitter<any>();
  @Output() selectFocused = new EventEmitter<void>();

  ngOnInit() {
    if (this.isNotEmpty(this.value)) this.control.patchValue(this.value);
  }

  writeValue(value: any): void {
    if (this.isNotEmpty(value)) {
      this.value = value;
    }
  }

  onSearch(event: any) {
    this.searchValueChanged.emit(event);
  }

  onClear() {
    this.valueCleared.emit();
  }

  onRemove(event: Event) {
    this.valueRemoved.emit(event);
  }

  onAdd(event: Event) {
    this.valueAdded.emit(event);
  }

  onScroll(event: any) {
    this.scrolled.emit(event);
  }

  onOpen() {
    this.selectFocused.emit();
  }

  onChange = (value: any) => {};

  onTouched = () => {};

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onChangeSelect(value: any): void {
    if (value !== undefined && value !== null && value !== '') {
      this.onChange(value);
      this.valueChanged.emit(value);
    }
  }

  customSearchFn(term: string, item: any): boolean {
    term = term?.toLowerCase().trim();
    return (
      item?.name?.toLowerCase().includes(term) ||
      item?.fullName?.toLowerCase().includes(term) ||
      item?.title?.toLowerCase().includes(term) ||
      item?.alpha2Code?.toLowerCase().includes(term) ||
      item?.alpha3Code?.toLowerCase().includes(term) ||
      item?.uinp?.toLowerCase().includes(term)
    );
  }

  isLastItem(item: any): boolean {
    if (!this.multiple || !this.preventRemoveLastItem) return false;
    const selectedItems = this.control.value || [];
    return selectedItems.length === 1 && selectedItems[0] === item.value;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    this.control = control as FormControl;
    return this.required && !control.value ? { required: true } : null;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      if (!this.control?.disabled) {
        this.control?.disable({ emitEvent: false });
      }
    } else {
      if (this.control?.disabled) {
        this.control?.enable({ emitEvent: false });
      }
    }
  }

  getErrorMessage() {
    const errors = this.control?.errors;
    const required = errors?.['required'];
    if (errors || this.required) {
      if (required || this.required) return 'Поле обязательно к заполнению';
    }
    return 'Error message';
  }

  getTitle(): string {
    if (this.control.value && this.items) {
      if (this.multiple) {
        return this.control.value
          .map((value: any) => this.findLabelByValue(value))
          .filter((label: string) => label !== undefined)
          .join(', ');
      } else {
        return this.findLabelByValue(this.control.value) || this.placeholder;
      }
    }
    return this.placeholder;
  }

  private findLabelByValue(value: any): string | undefined {
    const item = this.items.find((item) => item[this.bindValue] === value);
    return item ? item[this.bindLabel] : undefined;
  }

  isNotEmpty = (value: any) => value !== undefined && value !== null && value !== '';
}
